import React, { useState } from 'react';
import {  FiAlertCircle, FiPause, FiPlay, FiXCircle, FiX } from 'react-icons/fi';
import axiosInstance from '../../../libs/axios';

interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: 'hold' | 'cancel' | 'resume' | null;
  siteId: string;
  onSuccess: () => void;
}

const CancelHoldModal: React.FC<ActionModalProps> = ({ isOpen, onClose, action, siteId, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (action !== 'resume' && !reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    try {
      setLoading(true);
      setError('');

      let endpoint = '';
      let body = {};

      if (action === 'hold') {
        endpoint = `/survey/sites/${siteId}/hold`;
        body = { hold_reason: reason };
      } else if (action === 'cancel') {
        endpoint = `/survey/sites/${siteId}/cancel`;
        body = { cancel_reason: reason };
      } else if (action === 'resume') {
        endpoint = `/survey/sites/${siteId}/resume`;
        body = {};
      }

      await axiosInstance.put(endpoint, body);

      onSuccess();
      onClose();
      setReason('');
    } catch (err) {
      setError(`Failed to ${action} site. Please try again.`);
      console.error(`Error ${action}ing site:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const actionText = action === 'hold' ? 'Hold' : action === 'resume' ? 'Resume' : 'Cancel';
  const actionColor = action === 'hold' ? 'yellow' : action === 'resume' ? 'green' : 'red';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {actionText} Site
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {action !== 'resume' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for {actionText.toLowerCase()}ing this site *
              </label>
              <textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                placeholder={`Enter the reason for ${actionText.toLowerCase()}ing this site...`}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={loading}
                required
              />
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
              <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className={`p-3 ${action === 'hold' ? 'bg-yellow-50 border-yellow-200' : action === 'resume' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded`}>
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> {action === 'hold'
                ? 'This action will temporarily hold the site.'
                : action === 'resume'
                  ? 'This action will resume the site from hold status.'
                  : 'This action will permanently cancel the site. This action may not be reversible.'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || (action !== 'resume' && !reason.trim())}
            className={`flex-1 px-4 py-2 ${action === 'hold' ? 'bg-yellow-600 hover:bg-yellow-700' : action === 'resume' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white rounded transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              <>
                {action === 'hold' ? <FiPause size={16} /> : action === 'resume' ? <FiPlay size={16} /> : <FiXCircle size={16} />}
                {actionText} Site
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelHoldModal;